/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

import {
  Box,
  FormControlLabel,
  Grid,
  Hidden,
  InputAdornment,
  makeStyles,
  Switch,
  Typography,
} from "@material-ui/core";

import {
  ArrayField,
  ArrayInput,
  BooleanInput,
  Create,
  Datagrid,
  email,
  FileInput,
  FormDataConsumer,
  FormTab,
  FunctionField,
  maxLength,
  minLength,
  minValue,
  number,
  NumberInput,
  RadioButtonGroupInput,
  regex,
  required,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";
import setFieldData from "final-form-set-field-data";

import { AttachFileIcon } from "../../../components/common/iconography";
import { BackButton } from "../../../components/common/BackButton";
import { FileField } from "../../../components/common/FileField";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import CustomFieldsInput from "../../../components/common/CustomFieldsInput";
import { SmartTipIcon } from "../../../components/common/SmartTipIcon";

import {
  ACCEPTED_FILES,
  AccountType,
  CountryList,
  DefaultCarlotLotNumber,
  DefaultFifoLotNumber,
  InvoiceDefaultSubject,
  MAX_FILES,
  PODDefaultSubject,
  POSDefaultSubject,
  REGEX_MULTI_EMAIL,
  REGEX_PHONE,
  ServiceType,
  TrueOrFalse,
} from "../../../utils/constants";
import { handleFileChange } from "../../../utils/fileUtils";
import { UnitOptions } from "../../../utils/constants";
import {
  fetchTriggerEnabled,
  getTriggerEnabled,
} from "../../../utils/zapierUtils";

import { AccountCreateHelp } from "./AccountHelp";
import { CatalogUpload } from "./CatalogUploadDialog";
import { ContactTab } from "./AccountContactTab";
import {
  customHeadersToInventoryFields,
  getCustomHeaders,
  requiredEmail,
} from "./AccountUtils";
import { ValidateCreateButton } from "./ValidateCreateButton";
import { AccountSettingsTab } from "./AccountSettingsTab";
import { ServiceUpload } from "../services/ServiceUploadDialog";
import { ChargeRuleUpload } from "./chargeRules/ChargeRuleUploadDialog";
import { getDefaultTaxRate } from "../services/ServiceUtils";
import { ImportServicesMenuButton } from "../services/ImportServicesMenuButton";

const useStyles = makeStyles((theme) => ({
  arrayInput: {
    marginTop: theme.spacing(-2),
  },
}));

const validateRequired = required();
const validateMultiEmail = [regex(REGEX_MULTI_EMAIL, "Must be valid email(s)")];
const validateSyncedBillingEmail = [requiredEmail, ...validateMultiEmail];
const validateOptEmail = [email()];
const validateOptPhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];
const validateState = [required(), minLength(2), maxLength(2)];
const validateZip = [required(), minLength(5)];
const validateCreditLimit = [number(), minValue(0)];

const BillingSetupTab = (props) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const [copyContactInfo, setCopyContactInfo] = useState(false);
  const [billingAddress, setBillingAddress] = useState({});
  const [billingEmail, setBillingEmail] = useState("");
  // A new account is pushed to the external system on save when sync is on.
  const [isSyncEnabled, setIsSyncEnabled] = useState(
    getTriggerEnabled("accountCreated"),
  );

  useEffect(() => {
    let active = true; // prevent memory leaks - setting state after component unmounts
    fetchTriggerEnabled("accountCreated").then((enabled) => {
      if (active) setIsSyncEnabled(enabled);
    });
    return () => (active = false);
  }, []);

  const termList = window.appUser?.reloadYard?.paymentTerms?.items || [];

  const handleBillingStreetChange = (event, formData) => {
    const newAddress = {
      ...formData.billingAddress,
      street: event.target.value,
    };
    setBillingAddress(newAddress);
  };

  const handleBillingCityChange = (event, formData) => {
    const newAddress = { ...formData.billingAddress, city: event.target.value };
    setBillingAddress(newAddress);
  };

  const handleBillingStateChange = (event, formData) => {
    const newAddress = {
      ...formData.billingAddress,
      state: event.target.value,
    };
    setBillingAddress(newAddress);
  };

  const handleBillingZipChange = (event, formData) => {
    const newAddress = {
      ...formData.billingAddress,
      zipCode: event.target.value,
    };
    setBillingAddress(newAddress);
  };

  const handleBillingCountryChange = (event, formData) => {
    const newAddress = {
      ...formData.billingAddress,
      country: event.target.value,
    };
    setBillingAddress(newAddress);
  };

  const handleBillingEmailChange = (event) => {
    setBillingEmail(event.target.value);
  };

  const handleCopyContactInfoChange = (event, formData) => {
    form.change(
      "billingEmail",
      event.target.checked ? formData.email : billingEmail,
    );
    form.change(
      "billingAddress",
      event.target.checked ? formData.address : billingAddress,
    );
    setCopyContactInfo(event.target.checked);
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container direction="column" spacing={0}>
          <Grid container item direction="row" spacing={2} xs={12}>
            <Grid item xs={3}>
              <RadioButtonGroupInput
                source="skipInvoice"
                label="Skip Billing"
                choices={TrueOrFalse}
                defaultValue={false}
                row
                style={{ marginTop: 0 }}
                helperText={false}
              />
            </Grid>
          </Grid>

          {!formData.skipInvoice && (
            <Grid container item direction="row" spacing={2} xs={12}>
              <Grid item xs={12} md={3}>
                <TextInput
                  source="einNumber"
                  label="EIN/Tax ID Number"
                  variant="outlined"
                  fullWidth
                  helperText={false}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SelectInput
                  source="paymentTermId"
                  label="Payment Terms"
                  variant="outlined"
                  fullWidth
                  choices={termList}
                  validate={validateRequired}
                  helperText={false}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <NumberInput
                  source="creditLimit"
                  label="Credit Limit"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"> $ </InputAdornment>
                    ),
                    inputMode: "decimal",
                  }}
                  validate={validateCreditLimit}
                  placeholder="No limit"
                  helperText={false}
                />
                <SmartTipIcon label="credit-limit-icon" mt={-1} />
              </Grid>

              <Grid item xs={12} lg={9}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      Billing Address
                    </Typography>
                  </legend>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        label="Copy Account Contact Info"
                        style={{ marginLeft: "0px" }}
                        control={
                          <Switch
                            name="copyContactInfo"
                            size="small"
                            checked={copyContactInfo}
                            disabled={!formData.address || !formData.email}
                            onChange={(event) =>
                              handleCopyContactInfoChange(event, formData)
                            }
                          />
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        // The sync flag settles after the form registers; a
                        // remount re-runs validation against the new rules.
                        key={
                          isSyncEnabled ? "billingEmail-synced" : "billingEmail"
                        }
                        source="billingEmail"
                        label="Billing email"
                        type="email"
                        variant="outlined"
                        fullWidth
                        parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
                        onChange={(event) => handleBillingEmailChange(event)}
                        validate={
                          isSyncEnabled
                            ? validateSyncedBillingEmail
                            : validateMultiEmail
                        }
                        helperText="Comma-separated email(s)"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box mt={1}>
                        <BooleanInput
                          source="invoiceAttachments"
                          color="primary"
                          label="Enclose Attachments"
                          helperText={false}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="billingAddress.name"
                        label="Business Name"
                        variant="outlined"
                        defaultValue={formData.businessName}
                        fullWidth
                        disabled
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="billingAddress.street"
                        label="Street Address"
                        variant="outlined"
                        fullWidth
                        validate={validateRequired}
                        onChange={(event) =>
                          handleBillingStreetChange(event, formData)
                        }
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextInput
                        source="billingAddress.city"
                        label="City"
                        variant="outlined"
                        fullWidth
                        validate={validateRequired}
                        onChange={(event) =>
                          handleBillingCityChange(event, formData)
                        }
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <TextInput
                        source="billingAddress.state"
                        label="State"
                        variant="outlined"
                        fullWidth
                        parse={(v) => (v !== "" ? v.toUpperCase() : undefined)}
                        validate={validateState}
                        inputProps={{ maxLength: 2 }}
                        onChange={(event) =>
                          handleBillingStateChange(event, formData)
                        }
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={8} md={2}>
                      <TextInput
                        source="billingAddress.zipCode"
                        label="Zip Code"
                        variant="outlined"
                        fullWidth
                        validate={validateZip}
                        onChange={(event) =>
                          handleBillingZipChange(event, formData)
                        }
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <SelectInput
                        source="billingAddress.country"
                        label="Country"
                        variant="outlined"
                        fullWidth
                        choices={CountryList}
                        validate={validateRequired}
                        onChange={(event) =>
                          handleBillingCountryChange(event, formData)
                        }
                        helperText={false}
                      />
                    </Grid>
                  </Grid>
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      CUSTOM FIELDS
                    </Typography>
                  </legend>
                  <CustomFieldsInput
                    {...props}
                    source="invoiceCustomFields"
                    displayValues={[]}
                    className={classes.arrayInput}
                  />
                </fieldset>
              </Grid>
              {!formData.skipInvoice && (
                <Grid item xs={12}>
                  <fieldset>
                    <legend>
                      <Typography variant="overline" color="textSecondary">
                        BILLING CONTACTS
                      </Typography>
                    </legend>
                    <ArrayInput
                      source="billingContacts"
                      label=""
                      fullWidth
                      className={classes.arrayInput}
                    >
                      <SimpleFormIterator>
                        <FormDataConsumer>
                          {({
                            // formData, // The whole form data served above
                            scopedFormData, // The data for this item of the ArrayInput
                            getSource, // Function to get the valid source inside an ArrayInput. Translates to "inventories.items[].property"
                            ...rest
                          }) => (
                            <Grid container direction="row" spacing={2}>
                              <Grid item xs={12} lg={3}>
                                <TextInput
                                  source={getSource("name")}
                                  label="Contact Name"
                                  variant="outlined"
                                  fullWidth
                                  validate={validateRequired}
                                  helperText={false}
                                />
                              </Grid>
                              <Grid item xs={12} lg={3}>
                                <TextInput
                                  source={getSource("email")}
                                  label="Email"
                                  type="email"
                                  variant="outlined"
                                  fullWidth
                                  parse={(v) =>
                                    v !== "" ? v.toLowerCase() : undefined
                                  }
                                  validate={validateOptEmail}
                                  helperText={false}
                                />
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <TextInput
                                  source={getSource("phone")}
                                  label="Phone"
                                  variant="outlined"
                                  fullWidth
                                  validate={validateOptPhone}
                                  helperText={false}
                                />
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <TextInput
                                  source={getSource("description")}
                                  label="Description"
                                  variant="outlined"
                                  fullWidth
                                  helperText={false}
                                />
                              </Grid>
                            </Grid>
                          )}
                        </FormDataConsumer>
                      </SimpleFormIterator>
                    </ArrayInput>
                  </fieldset>
                </Grid>
              )}
              <Hidden xsDown>
                <Grid item xs={12}>
                  <FileInput
                    source="attachments"
                    label="ATTACHMENTS"
                    variant="outlined"
                    fullWidth
                    className={classes.fileInput}
                    placeholder={
                      <p>
                        <AttachFileIcon fontSize="large" /> <br /> Drop file(s)
                        to upload, or click to select.
                      </p>
                    }
                    accept={ACCEPTED_FILES}
                    multiple
                    maxFiles={MAX_FILES}
                    options={{ maxFiles: MAX_FILES }}
                    onChange={(value) =>
                      handleFileChange("attachments", value, form, notify)
                    }
                  >
                    <FileField source="src" title="title" target="_blank" />
                  </FileInput>
                </Grid>
              </Hidden>
            </Grid>
          )}
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const ServicesTab = ({
  allServices,
  setAllServices,
  csvErrors,
  setCSVErrors,
  ...props
}) => {
  const [csvData, setCSVData] = useState({});

  useEffect(() => {
    if (csvData.services) {
      setAllServices(csvData.services);
    }
  }, [csvData.services]);

  return (
    <>
      <TopToolbar>
        <ImportServicesMenuButton
          existingServices={allServices}
          enableCSV={false}
          onCreateMerge={(merged) => setAllServices(merged)}
        />
      </TopToolbar>
      <ServiceUpload
        csvData={csvData}
        setCSVData={setCSVData}
        csvErrors={csvErrors}
        setCSVErrors={setCSVErrors}
        hidePreviewIfNoErrors={true}
      />
      {(!csvErrors || csvErrors.length === 0) && allServices?.length > 0 && (
        <>
          <Typography variant="overline" color="textSecondary" gutterBottom>
            Imported Services
          </Typography>
          <ArrayField source="items" record={{ items: allServices }}>
            <Datagrid>
              <TextField source="name" label="Service Name" />
              <TextField source="description" label="Description" />
              <FunctionField
                label="Tax Code"
                render={(record) =>
                  record?.taxCode || record?.taxRate?.taxCode || ""
                }
              />
              <FunctionField
                label="Tax Rate %"
                render={(record) => {
                  const v = record?.taxRate;
                  if (typeof v === "number") return v;
                  if (v && typeof v === "object") return v.taxRate ?? "";
                  return "";
                }}
              />
              {UnitOptions.map((unit) => (
                <FunctionField
                  key={unit.column}
                  source={unit.column}
                  label={unit.column}
                  render={(record) =>
                    record?.[unit.column] ? record[unit.column] : ""
                  }
                />
              ))}
            </Datagrid>
          </ArrayField>
        </>
      )}
    </>
  );
};

const ChargeRulesTab = ({
  csvErrors,
  setCSVErrors,
  allChargeRules,
  setAllChargeRules,
  allServices,
  ...props
}) => {
  const [csvData, setCSVData] = useState({});

  useEffect(() => {
    if (csvData.rules) {
      setAllChargeRules(csvData.rules);
    }
  }, [csvData.rules]);

  return (
    <ChargeRuleUpload
      csvData={csvData}
      setCSVData={setCSVData}
      csvErrors={csvErrors}
      setCSVErrors={setCSVErrors}
      existingServices={allServices}
    />
  );
};

const CatalogTab = ({
  csvErrors,
  setCSVErrors,
  allProducts,
  setAllProducts,
  ...props
}) => {
  const form = useForm();
  const [csvData, setCSVData] = useState({});

  useEffect(() => {
    if (csvData.products) {
      const customHeaders = getCustomHeaders(csvData.products[0]);
      customHeadersToInventoryFields(form, customHeaders);
      setAllProducts(csvData.products);
    }
  }, [csvData.products]);

  return (
    <CatalogUpload
      csvData={csvData}
      setCSVData={setCSVData}
      csvErrors={csvErrors}
      setCSVErrors={setCSVErrors}
    />
  );
};

const AccountCreateActions = ({ basePath, data, resource, ...props }) => (
  <TopToolbar>
    <HelpButton>
      <AccountCreateHelp {...props} />
    </HelpButton>
    <BackButton basePath={basePath} />
  </TopToolbar>
);

const AccountCreateToolbar = ({
  allServices,
  allChargeRules,
  allProducts,
  ...props
}) => {
  return (
    <Toolbar {...props}>
      <ValidateCreateButton
        label="Save"
        allServices={allServices}
        allChargeRules={allChargeRules}
        allProducts={allProducts}
      />
    </Toolbar>
  );
};

const validateAccountCreate = (csvErrors) => {
  const errors = {};
  if (csvErrors) errors.productFile = " ";

  return errors;
};

const defaultTaxRate = getDefaultTaxRate(window.appUser?.reloadYard);
const paymentTerms = window.appUser?.reloadYard?.paymentTerms.items || [];
const defaultPaymentTermId =
  paymentTerms.length === 1 ? paymentTerms[0].id : "";

const initialValues = {
  externalId: " ",
  paymentTermId: defaultPaymentTermId,
  accountType: AccountType.CUSTOMER,
  skipInvoice: false, // required for formData.skipInvoice to work
  // cannot use defaultValue - must be able to delete all chips
  podSubject: JSON.stringify(PODDefaultSubject),
  posSubject: JSON.stringify(POSDefaultSubject),
  invoiceSubject: JSON.stringify(InvoiceDefaultSubject),
  fifoLotNumber: JSON.stringify(DefaultFifoLotNumber),
  carlotLotNumber: JSON.stringify(DefaultCarlotLotNumber),
  services: [
    {
      name: ServiceType.ACCESSORIAL_CHARGES,
      description: "",
      taxRate: defaultTaxRate,
    },
  ],
};

export const AccountCreate = (props) => {
  const [csvErrors, setCSVErrors] = useState();
  const [allServices, setAllServices] = useState(initialValues.services);
  const [allChargeRules, setAllChargeRules] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  return (
    <Create
      {...props}
      title={<ForceTitle title="New Account" />}
      actions={<AccountCreateActions {...props} />}
    >
      <TabbedForm
        variant="outlined"
        submitOnEnter={false}
        initialValues={initialValues}
        toolbar={
          <AccountCreateToolbar
            allServices={allServices}
            allChargeRules={allChargeRules}
            allProducts={allProducts}
          />
        }
        mutators={{ setFieldData }}
        validate={() => validateAccountCreate(csvErrors)}
      >
        <FormTab path="" label="Contact Info">
          <ContactTab />
        </FormTab>
        <FormTab path="invoiceSetup" label="Billing Setup">
          <BillingSetupTab />
        </FormTab>
        <FormTab path="serviceRates" label="Service Rates">
          <ServicesTab
            csvErrors={csvErrors}
            setCSVErrors={setCSVErrors}
            allServices={allServices}
            setAllServices={setAllServices}
          />
        </FormTab>
        <FormTab
          path="billingRules"
          label="Billing Rules"
          // label={
          //   <span>
          //     Billing Rules <BetaBadge />
          //   </span>
          // }
        >
          <ChargeRulesTab
            csvErrors={csvErrors}
            setCSVErrors={setCSVErrors}
            allChargeRules={allChargeRules}
            setAllChargeRules={setAllChargeRules}
            allServices={allServices}
          />
        </FormTab>
        <FormTab path="customSettings" label="Custom Settings">
          <AccountSettingsTab />
        </FormTab>
        <FormTab path="productCatalog" label="Product Catalog">
          <CatalogTab
            csvErrors={csvErrors}
            setCSVErrors={setCSVErrors}
            allProducts={allProducts}
            setAllProducts={setAllProducts}
          />
        </FormTab>
      </TabbedForm>
    </Create>
  );
};
